import {
  isPayablePricingDisplayContract,
  type PricingDisplayContract,
} from '@/lib/billing/pricingDisplay';
import { formatSarAmount } from '@/lib/canonicalMoney';

export const CONSENT_TEXT_VERSION = 'v4.0-recurring-cancel-resume-binding';
export type ConsentLanguage = 'en' | 'ar';

export function getConsentText(props: {
  planName: string;
  billingCycle: string;
  language?: string;
  introPricing?: PricingDisplayContract | null;
  renewalPricing: PricingDisplayContract;
}) {
  const language = props.language?.toLowerCase().startsWith('ar') ? 'ar' : 'en';
  const yearly =
    props.billingCycle === 'yearly' || props.billingCycle === 'annual';
  const frequency = yearly
    ? (language === 'ar' ? 'سنوية' : 'annual')
    : (language === 'ar' ? 'شهرية' : 'monthly');
  const intro = props.introPricing
    ? language === 'ar'
      ? ` الرسوم التمهيدية الإجمالية هي ${formatSarAmount(props.introPricing.gross_amount_sar, 'consent_intro_gross')} ر.س، شاملة ضريبة القيمة المضافة.`
      : ` The introductory gross charge is SAR ${formatSarAmount(props.introPricing.gross_amount_sar, 'consent_intro_gross')}, including VAT.`
    : '';
  return language === 'ar'
    ? `بالضغط على "تأكيد ودفع"، أوافق على رسوم اشتراك ${frequency} متكررة.${intro} يتجدد الاشتراك تلقائيًا بالمبلغ الإجمالي ${formatSarAmount(props.renewalPricing.gross_amount_sar, 'consent_renewal_gross')} ر.س، شامل ضريبة القيمة المضافة، حتى ألغي الاشتراك. يوقف الإلغاء عملية التجديد التالية، وتظل الخدمة المدفوعة فعالة حتى نهاية الفترة المدفوعة الحالية.`
    : `By clicking Confirm & Pay, I consent to recurring ${frequency} subscription charges.${intro} The subscription automatically renews at the gross amount SAR ${formatSarAmount(props.renewalPricing.gross_amount_sar, 'consent_renewal_gross')}, including VAT, until I cancel. Cancellation stops the next renewal, and paid service remains active through the current paid-through date.`;
}

export interface BillingConsentBindingRequest {
  accepted: true;
  consent_language: ConsentLanguage;
  consent_text_version: typeof CONSENT_TEXT_VERSION;
  plan_id: string;
  billing_cycle: 'monthly' | 'yearly';
  tax_config_id: string;
  due_pricing: PricingDisplayContract;
  intro_pricing: PricingDisplayContract | null;
  renewal_pricing: PricingDisplayContract;
}

export function buildBillingConsentBinding(input: {
  planId: string;
  billingCycle: 'monthly' | 'yearly';
  taxConfigId: string | null | undefined;
  duePricing: PricingDisplayContract | null | undefined;
  introPricing?: PricingDisplayContract | null;
  renewalPricing: PricingDisplayContract | null | undefined;
  consentLanguage: string | null | undefined;
}): BillingConsentBindingRequest | null {
  const consentLanguage = input.consentLanguage?.toLowerCase().startsWith('ar')
    ? 'ar'
    : input.consentLanguage?.toLowerCase().startsWith('en')
      ? 'en'
      : null;
  if (
    !input.planId ||
    !input.taxConfigId ||
    !isPayablePricingDisplayContract(input.duePricing) ||
    !isPayablePricingDisplayContract(input.renewalPricing) ||
    (input.introPricing != null && !isPayablePricingDisplayContract(input.introPricing)) ||
    !consentLanguage
  ) {
    return null;
  }
  return {
    accepted: true,
    consent_language: consentLanguage,
    consent_text_version: CONSENT_TEXT_VERSION,
    plan_id: input.planId,
    billing_cycle: input.billingCycle,
    tax_config_id: input.taxConfigId,
    due_pricing: input.duePricing,
    intro_pricing: input.introPricing ?? null,
    renewal_pricing: input.renewalPricing,
  };
}
